import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Alert } from 'react-native';

import { supabase } from '../../../lib/supabase';
import type { GameRoom } from '../../../types/multiplayer';
import { useUserProfile } from '../../quiz-core/useStore';

import { useRoomChannel } from './useRoomChannel';
import { useRoomChat } from './useRoomChat';
import { useRoomPlayers } from './useRoomPlayers';
import { useRoomPresence } from './useRoomPresence';
import { useRoomState } from './useRoomState';
import { uiText } from '../../../shared/utils/uiText';


type RootStackParamList = {
  GameRoom: {
    roomId: string;
    familyId?: string;
    familyName?: string;
    friendsGroupId?: string;
    friendsGroupName?: string;
  };
  MultiplayerGame: { roomId: string };
  MultiplayerResults: {
    roomId: string;
    sessionId: string;
    familyId?: string;
    familyName?: string;
    friendsGroupId?: string;
    friendsGroupName?: string;
  };
  MultiplayerQuiz: {
    roomId: string;
    sessionId: string;
    multiplayer: boolean;
    category?: string;
    categoryId: number;
    difficulty_id: number;
    questionCount: number;
    familyId?: string;
    familyName?: string;
    friendsGroupId?: string;
    friendsGroupName?: string;
  };
  // Add other screen params as needed
};

type GameRoomScreenNavigationProp = StackNavigationProp<RootStackParamList, 'GameRoom'>;

interface UseGameRoomGroupContext {
  familyId?: string;
  familyName?: string;
  friendsGroupId?: string;
  friendsGroupName?: string;
}

export function useGameRoom(roomId: string, groupContext: UseGameRoomGroupContext = {}) {
  const navigation = useNavigation<GameRoomScreenNavigationProp>();
  const profile = useUserProfile();
  const channel = useRoomChannel(roomId);
  const startedSessionRef = useRef<string | null>(null);
  
  // Initialize chat hooks first to avoid circular dependencies
  const chatHooks = useRoomChat(roomId, profile?.id || '', channel);
  const { 
    messages = [], 
    isTyping = false, 
    sendMessage = async () => {}, 
    handleTyping = () => {}
  } = chatHooks || {};
  
  // Use useRoomPlayers for accurate player data with profiles
  const { players, syncPlayers } = useRoomPlayers({ roomId, userId: profile?.id || null });
  // Still use presence for online status tracking
  const { presenceReady, updatePresence } = useRoomPresence(roomId, profile?.id || '', channel);
  
  // CRITICAL: Define handleGameStart before useRoomState to enable real-time navigation
  // Handle game start callback
  const handleGameStart = useCallback(async (sessionId: string) => {
    console.log('🎯 [handleGameStart] CALLBACK TRIGGERED!', {
      sessionId,
      roomId,
      timestamp: new Date().toISOString(),
      callStack: new Error().stack?.split('\n').slice(0, 5)
    });
    
    if (!sessionId) {
      console.error('❌ [handleGameStart] No session ID provided to handleGameStart');
      return;
    }
    if (!roomId) {
      console.error('❌ [handleGameStart] Missing roomId');
      setStartGameError('Missing room ID');
      return;
    }
    if (startedSessionRef.current === sessionId) {
      console.log('[handleGameStart] Ignoring duplicate start for session already handled', {
        sessionId,
        roomId,
      });
      return;
    }
    
    console.log('🚀 [handleGameStart] Preparing to navigate to game screen with session:', sessionId);
    setIsStartingGame(true);
    setStartGameError(null);
    
    try {
      // First, verify the session exists and is ready
      const { data: sessionData, error: sessionError } = await supabase
        .from('multiplayer_game_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
      
      if (sessionError || !sessionData) {
        throw new Error(sessionError?.message || 'Failed to verify game session');
      }
      // If the session is already completed, results/rematch flow should take over.
      // Do not interrupt the user with a dead-end alert from the room hook.
      if (sessionData.status === 'completed') {
        setIsStartingGame(false);
        setStartGameError(null);
        console.log('[handleGameStart] Session already completed, ignoring stale room callback', {
          sessionId,
          roomId,
        });
        return;
      }
      
      console.log('Game session verified, preparing navigation...');
      
      // Fetch current room data to get the actual difficulty and category
      const { data: roomData, error: roomError } = await supabase
        .from('multiplayer_game_rooms')
        .select('category_id, difficulty_id, difficulty, family_name')
        .eq('id', roomId)
        .single();

      if (roomError || !roomData) {
        console.warn('Could not fetch room data, using defaults:', roomError);
      }
      
      // Use room data if available, otherwise fallback to defaults
      const categoryId = roomData?.category_id ?? 5; // Default to Miracles
      const difficultyId = roomData?.difficulty_id ?? 2; // Use room difficulty or default to medium
      const categoryName = roomData?.family_name || 'Bible Trivia'; // Use family name or default
      
      // Map difficulty_id to difficulty string for backward compatibility with old rooms
      const difficultyMap: Record<number, string> = {
        1: 'easy',
        2: 'medium', 
        3: 'hard'
      };
      
      // Use difficulty string from database if available, otherwise map from difficulty_id
      const difficulty = roomData?.difficulty || difficultyMap[difficultyId] || 'medium';
      
      console.log('🎯 [handleGameStart] Using room data:', { 
        categoryId, 
        difficultyId, 
        difficulty,
        categoryName 
      });
      // Prepare navigation params
      const params = { 
        roomId,
        sessionId,
        multiplayer: true as const,
        category: categoryName,
        categoryId,
        difficulty: difficulty, // Use string difficulty for timer calculation
        difficulty_id: difficultyId, // Keep numeric ID for backward compatibility
        questionCount: 10, // Default question count
        ...groupContext,
      };

      // Update total_questions for all players in this room
      try {
        // Dynamically determine game_type based on currentRoom context
const gameType = 'family'; // Default. Extend logic if/when GameRoom adds more group fields.
// If you add friends_id, church_id, or team_id to GameRoom in the future, update this logic accordingly.
// For now, use only available fields.
await supabase
  .from('multiplayer_game_room_players')
  .update({ 
    total_questions: params.questionCount, 
    game_type: gameType
  })
  .eq('room_id', roomId);
        console.log('Updated total_questions for all players in room', roomId);
      } catch (updateErr) {
        console.error('Failed to update total_questions for players:', updateErr);
      }

      console.log('🎮 [handleGameStart] Navigation params ready, navigating to game...', params);
      
      // Navigate to the Game screen with the required parameters
      console.log('🔄 [handleGameStart] Calling navigation.navigate with MultiplayerQuiz');
      startedSessionRef.current = sessionId;
      navigation.navigate('MultiplayerQuiz', params);
      console.log('✅ [handleGameStart] Navigation.navigate called successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start game';
      console.error('Error starting game:', errorMessage, error);
      setStartGameError(errorMessage);
      
      // Fallback to minimal params if something goes wrong
      try {
        navigation.navigate('MultiplayerQuiz', {
          roomId,
          sessionId,
          multiplayer: true,
          category: 'Bible Trivia',
          categoryId: 5, // Miracles category has all question formats
          difficulty_id: 2,
          questionCount: 10
        });
      } catch (fallbackError) {
        console.error('Fallback navigation failed:', fallbackError);
        Alert.alert(uiText('errorTitle'), uiText('startGameFailed'));
      }
    } finally {
      setIsStartingGame(false);
    }
  }, [groupContext, roomId, navigation]); // currentRoom will be accessed dynamically from roomState
  
  // You may need to keep currentRoom, isHost, isReady, isLoading, etc. from useRoomState if they are not player state
  const roomState = useRoomState(roomId, profile?.id || '', channel, handleGameStart);
  const { currentRoom = null as GameRoom | null, isHost = false, isReady = false, isLoading: roomIsLoading = false, toggleReady: roomToggleReady = async () => false, startGame: roomStartGame = async () => {} } = roomState || {};
  
  // Sync players when room is ready
  useEffect(() => {
    if (roomId && profile?.id) {
      console.log('[useGameRoom] Syncing players for room:', roomId);
      syncPlayers();
    }
  }, [roomId, profile?.id, syncPlayers]);
  // Local state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [startGameError, setStartGameError] = useState<string | null>(null);
  
  // Handle toggle ready with proper error handling
  const toggleReady = useCallback(async () => {
    try {
      setIsProcessing(true);
      const newStatus = await roomToggleReady();
      await updatePresence(newStatus);
      return newStatus;
    } catch (error) {
      console.error('Error toggling ready status:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [roomToggleReady, updatePresence]);

  useEffect(() => {
    if (currentRoom?.status === 'completed') {
      startedSessionRef.current = null;
      return;
    }

    if (currentRoom?.status === 'in_progress' && currentRoom?.current_session_id) {
      console.log('Session started, navigating to game...');
      handleGameStart(currentRoom.current_session_id);
    }
  }, [currentRoom?.current_session_id, currentRoom?.status, handleGameStart]);

  // Handle sending a chat message
  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;
    
    try {
      await sendMessage(content);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [sendMessage]);
  
  // Handle starting the game (host only)
  const handleStartGame = useCallback(async () => {
    const currentPlayerIsInRoom = Boolean(profile?.id && players.some((player) => player.id === profile.id));
    if (!currentPlayerIsInRoom) {
      throw new Error('Only players already in this room can start the game');
    }
    
    if (!currentRoom) {
      throw new Error('Room not found');
    }
    
    try {
      setIsStartingGame(true);
      setStartGameError(null);
      
      // Get the category ID as a number if it exists
      const categoryId = currentRoom.category_id 
        ? (Array.isArray(currentRoom.category_id) 
            ? parseInt(currentRoom.category_id[0] as string, 10) 
            : parseInt(currentRoom.category_id as string, 10))
        : 1; // Default to 1 (General Knowledge)
      
      // Use the numeric difficulty_id directly from the currentRoom object
      // Default to 2 (medium) if not present, though it should always be there.
      const difficultyId = currentRoom.difficulty_id ?? 2;
      
      console.log('Starting game with params:', { 
        roomId, 
        categoryId, 
        difficultyId 
      });
      
      // This will trigger the handleGameStart callback when the session is ready
      await roomStartGame(roomId, categoryId, difficultyId);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start game';
      console.error('Error in handleStartGame:', errorMessage, error);
      setStartGameError(errorMessage);
      throw error; // Re-throw to be handled by the UI
    } finally {
      setIsStartingGame(false);
    }
  }, [currentRoom, players, profile?.id, roomStartGame]);
  
  // Handle leaving the room
  const handleLeaveRoom = useCallback(async () => {
    try {
      // Add any cleanup needed when leaving the room
      navigation.goBack();
    } catch (error) {
      console.error('Error leaving room:', error);
      throw error;
    }
  }, [navigation]);
  
  // Return all the necessary values and functions with proper types
  // Debug logs for every render
  console.log('[useGameRoom] render', {
    presenceReady,
    roomIsLoading,
    isStartingGame,
    isProcessing,
    isLoading: roomIsLoading || isStartingGame || isProcessing || !presenceReady,
    currentRoom,
    players,
    isHost,
    isReady
  });

  const result = {
    // Room state
    currentRoom,
    players,
    isHost,
    isReady,
    isLoading: roomIsLoading || isStartingGame || isProcessing,
    presenceReady,

    // Chat
    messages,
    isTyping,
    sendMessage: handleSendMessage,
    handleTyping,
    
    // Game controls
    startGame: handleStartGame,
    toggleReady,
    
    // User info
    currentUser: profile,
    
    // Status and errors
    error: startGameError,
    isStartingGame,
    isProcessing,
    
    // Navigation
    handleLeaveRoom,
    
    // Aliases for backward compatibility
    handleToggleReady: toggleReady,
    handleStartGame: handleStartGame,
    handleSendMessage: handleSendMessage
  } as const;
  
  return result;
}
